/**
 * AuthorOnboardingStatusBanner — Phase A creator-centric refactor.
 *
 * Shown on top of ContentPlansPage when:
 *   (a) author doesn't exist yet → CTA "Setup author profile"
 *   (b) author exists but isComplete=false → CTA "Finish author profile"
 *
 * Testid: author-onboarding-status-banner
 */

import React from 'react';
import { Alert, Button, Typography, Box } from '@mui/material';
import { useAuthor } from '../../hooks/useAuthor';

const TESTID = 'author-onboarding-status-banner';

export interface AuthorOnboardingStatusBannerProps {
  projectId: string;
  onSetupClick: () => void;
}

export function AuthorOnboardingStatusBanner(
  props: AuthorOnboardingStatusBannerProps,
) {
  const { projectId, onSetupClick } = props;
  const { author, isComplete, loading } = useAuthor(projectId);

  if (loading) return null;
  if (isComplete) return null;

  const hasAuthor = !!author;
  const title = hasAuthor
    ? 'Finish setting up your author profile'
    : 'Setup your author profile to unlock content generation';
  const subtitle = hasAuthor
    ? 'Some identity fields are missing. AI generation uses these to write in your voice.'
    : 'AI will adapt voice, pillars, and examples from your LinkedIn / X profile — takes ~30 seconds.';

  return (
    <Alert
      severity={hasAuthor ? 'warning' : 'info'}
      data-testid={TESTID}
      action={
        <Button variant="contained" size="small" onClick={onSetupClick}>
          {hasAuthor ? 'Finish setup' : 'Setup author'}
        </Button>
      }
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Alert>
  );
}
